import React, { FC } from 'react';
import { Col, Input, InputNumber, Row, Typography } from 'antd';
import { Coords } from 'google-map-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import cx from 'classnames';
import { isEmpty } from 'lodash';

import { CountryInput, ExpansionForm, Form, GoogleMap, MapMarker, MCCInput } from '~/components';
import { Location } from '~/types';
import { locations } from '~/routes';
import { GOOGLE_MAP_API_KEY } from '~/env';
import { useStatefulForm } from '~/hooks/form';
import WorkingHoursInput from './WorkingHoursInput';
import ImageInput from '~/components/inputs/ImageInput';

interface LocationFormProps {
  name: typeof locations;
  initialValues?: Location;
  edit?: boolean;
}

const LocationForm: FC<LocationFormProps> = (props) => {
  const { initialValues, name } = props;
  const [t] = useTranslation('locations');
  const dt = (key: string): string => t(`translation:${key}`);
  const { formState, handleFormChange, form } = useStatefulForm(initialValues);

  return (
    <Form
      form={form}
      layout="vertical"
      name={name}
      onValuesChange={(_, newValues) => handleFormChange(newValues)}
    >
      <Form.Item name="name" label={dt('name')} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="merchantCategoryCode"
        label={t('merchantCategoryCode')}
        rules={[{ required: true }]}
      >
        <MCCInput />
      </Form.Item>
      <Typography.Title level={5}>{dt('address')}</Typography.Title>
      <Form.Item name={['address', 'country']} rules={[{ required: true }]}>
        <CountryInput />
      </Form.Item>
      <Form.Item name={['address', 'postalCode']}>
        <Input placeholder={dt('postalCode')} />
      </Form.Item>
      <Form.Item name={['address', 'region']}>
        <Input placeholder={dt('region')} />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={['address', 'city']}>
            <Input placeholder={dt('city')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={['address', 'cityLocalized']}>
            <Input placeholder={dt('cityLocalized')} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name={['address', 'addressLine']}>
        <Input.TextArea placeholder={dt('addressLine')} />
      </Form.Item>
      <ExpansionForm
        label={t('geolocation')}
        defaultOpen={!!initialValues?.address?.geolocation}
        tooltip={GOOGLE_MAP_API_KEY && t('geolocationHint')}
      >
        <GeolocationInput
          geolocation={formState?.address?.geolocation}
          defaultCountry={formState?.address?.country}
        />
      </ExpansionForm>
      <ExpansionForm
        label={t('Extra notes to address')}
        defaultOpen={!!initialValues?.address?.note}
      >
        <Form.Item name={['address', 'note']}>
          <Input.TextArea placeholder={t('Add notes how to find the place')} />
        </Form.Item>
      </ExpansionForm>
      <ExpansionForm
        label={t('Icon')}
        defaultOpen={!!initialValues?.branding?.icon}
        tooltip={t(
          'If unique icon is defined for location it will be shown instead of company’s icon.'
        )}
      >
        <Form.Item name={['branding', 'icon']}>
          <ImageInput label={t('Upload')} name="icon" multiple={false} variant="drawer" anonymous />
        </Form.Item>
      </ExpansionForm>
      <ExpansionForm
        label={t('Photos')}
        defaultOpen={!!initialValues?.branding?.photos}
        tooltip={t(
          'Add photos that will help your visitors to find the place. Max. {{maxCount}} photos allowed.',
          { maxCount }
        )}
      >
        <Form.Item name={['branding', 'photos']}>
          <ImageInput
            label={t('Upload')}
            name="photos"
            maxCount={maxCount}
            variant="drawer"
            anonymous
          />
        </Form.Item>
      </ExpansionForm>
      <ExpansionForm
        label={t('Working hours')}
        defaultOpen={!isEmpty(initialValues?.schedule)}
        tooltip={t(
          'Select days when location works and add one or more time ranges that do not intersect.'
        )}
      >
        <WorkingHoursInput initialValues={initialValues} formState={formState} form={form} />
      </ExpansionForm>
    </Form>
  );
};

const maxCount = 5;

interface GeolocationInputProps {
  className?: string;
  geolocation: Partial<Required<Location>['address']['geolocation']> | undefined;
  defaultCountry?: string;
}

const GeolocationInputNotStyled: FC<GeolocationInputProps> = (props) => {
  const { className, geolocation, defaultCountry } = props;
  const [t] = useTranslation('locations');

  return (
    <div className={cx(className, 'GeolocationInput')}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={latName}
            rules={[{ type: 'number', min: -90, max: 90 }, { required: !!geolocation?.longitude }]}
          >
            <InputNumber style={fullWidth} step={0.1} placeholder={t('latitude')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={lngName}
            rules={[{ type: 'number', min: -180, max: 180 }, { required: !!geolocation?.latitude }]}
          >
            <InputNumber style={fullWidth} step={0.1} placeholder={t('longitude')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24} className="GeolocationInput-Map">
          <Form.Item name={['address', 'geolocation']} noStyle>
            <GoogleMapInput defaultCountry={defaultCountry} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

interface GoogleMapInputProps {
  value?: Required<Location>['address']['geolocation'];
  onChange?: (value: Required<Location>['address']['geolocation']) => void;
  defaultCountry?: string;
}

const GoogleMapInput: FC<GoogleMapInputProps> = (props) => {
  const { value, onChange, defaultCountry } = props;

  const geolocation = {
    lat: value?.latitude || 0,
    lng: value?.longitude || 0,
  };

  const handleMapClick = ({ lat, lng }: Coords) => {
    onChange?.({ latitude: lat, longitude: lng });
  };

  return (
    <GoogleMap
      onClick={handleMapClick}
      center={geolocation}
      options={{ fullscreenControl: false }}
      zoom={14}
      defaultCountry={defaultCountry}
    >
      {geolocation ? <MapMarker lat={geolocation.lat} lng={geolocation.lng} /> : null}
    </GoogleMap>
  );
};

const latName = ['address', 'geolocation', 'latitude'];
const lngName = ['address', 'geolocation', 'longitude'];

const GeolocationInput = styled(GeolocationInputNotStyled)`
  .GeolocationInput-Map {
    width: 100%;
    height: 200px;
    margin-bottom: 24px;
  }
`;

const fullWidth = { width: '100%' };

export default LocationForm;
